import { Controller } from "@hotwired/stimulus"

/**
 * Payment Form Controller
 *
 * Handles Stripe tokenization for payment forms on account/plan and account/upgrade.
 * Connects when the form enters the DOM (including after Turbo navigation) and
 * sets up the submit handler. Cleans up on disconnect.
 */
export default class extends Controller {
  connect() {
    const $ = window.$
    this.form = this.element
    this.$form = $(this.form)
    this.submitButton = this.$form.find('button[type=submit], input[type=submit]').first()
    this.originalButtonText = this.submitButton.text() || this.submitButton.val()
    this.submitButton.attr('data-enable-with', this.originalButtonText)

    this.boundSubmitHandler = this.handleSubmit.bind(this)
    this.$form.on('submit', this.boundSubmitHandler)

    this.boundFocusHandler = this.handleInputFocus.bind(this)
    this.$form.on('focus', 'input[type=text], input[type=email], input[type=password]', this.boundFocusHandler)

    this.initStripe()
  }

  disconnect() {
    this.$form.off('submit', this.boundSubmitHandler)
    this.$form.off('focus', 'input[type=text], input[type=email], input[type=password]', this.boundFocusHandler)
  }

  handleInputFocus(e) {
    const $ = window.$
    const $el = $(e.currentTarget)
    $el.parent().removeClass('field_with_errors')
    const fieldId = $el.attr('id')
    if (fieldId === 'card_number_formatted') $('#card_number_error').hide()
    else if (fieldId === 'card_cvv_formatted') $('#card_cvv_error').hide()
    else if (fieldId === 'card_expiry_formatted') $('#card_expiry_error').hide()
    else if (fieldId === 'card_name') $('#card_name_error').hide()
  }

  initStripe(attempt = 0) {
    const $ = window.$
    const stripeKey = $('meta[name="stripe-key"]').attr('content')
    if (!stripeKey) return

    if (typeof window.Stripe !== 'undefined') {
      window.Stripe.setPublishableKey(stripeKey)
    } else if (attempt < 20) {
      setTimeout(() => this.initStripe(attempt + 1), 100)
    }
  }

  handleSubmit(e) {
    e.preventDefault()

    this.clearErrors()

    const error = this.validate()
    if (error) return false

    this.setButtonChecking()
    this.processCreditCard()
    return false
  }

  clearErrors() {
    this.$form.find('.field-error').hide().text('')
    this.$form.find('.field_with_errors').removeClass('field_with_errors')
    this.$form.find('#error_explanation').text('').hide()
  }

  validate() {
    let error = false
    const $ = window.$

    const cardNumberEl = this.form.querySelector('#card_number_formatted')
    const cardNumber = cardNumberEl?.cleave ? cardNumberEl.cleave.getRawValue() : $('#card_number_formatted').val()?.replace(/\s/g, '') || ''
    if (!cardNumber || cardNumber.length < 1) {
      error = true
      $('#card_number_formatted').parent().addClass('field_with_errors')
      $('#card_number_error').text('Card number is required').show()
    }

    const cardCvvEl = this.form.querySelector('#card_cvv_formatted')
    const cardCvv = cardCvvEl?.cleave ? cardCvvEl.cleave.getRawValue() : $('#card_cvv_formatted').val() || ''
    if (!cardCvv || cardCvv.length < 1) {
      error = true
      $('#card_cvv_formatted').parent().addClass('field_with_errors')
      $('#card_cvv_error').text('CVV is required').show()
    }

    if ($('#card_name').val()?.length < 1) {
      error = true
      $('#card_name').parent().addClass('field_with_errors')
      $('#card_name_error').text('Name on card is required').show()
    }

    const year = $('#card_year_hidden').val() || $('#card_year').val()
    if (!year || year.length < 1) {
      error = true
      $('#card_expiry_formatted').parent().addClass('field_with_errors')
      $('#card_expiry_error').text('Expiry year is required').show()
    }

    const month = $('#card_month_hidden').val() || $('#card_month').val()
    if (!month || month.length < 1) {
      error = true
      $('#card_expiry_formatted').parent().addClass('field_with_errors')
      $('#card_expiry_error').text('Expiry month is required').show()
    }

    if (!document.body.classList.contains('action-plan')) {
      if ($("[name='user[plan]']").length && $("[name='user[plan]']:checked").length < 1) {
        error = true
        $('#plan_error_explanation').show()
      } else if ($("[name='user[scheme_id]']").length && $("[name='user[scheme_id]']:checked").length < 1) {
        error = true
        $('#plan_error_explanation').show()
      }
    }

    if ($('#user_email').length && $('#user_email').val()?.length < 1) {
      error = true
      $('#user_email').parent().addClass('field_with_errors')
    }

    if ($('#user_password').length && $('#user_password').val()?.length < 6) {
      error = true
      $('#user_password').parent().addClass('field_with_errors')
      $('#error_explanation').text('Choose a password of at least 6 characters').show()
    }

    return error
  }

  setButtonChecking() {
    this.submitButton.text('Checking...')
    this.submitButton.val('Checking...')
    this.submitButton.attr('disabled', true)
  }

  resetButton() {
    const enableWith = this.submitButton.attr('data-enable-with') || 'Purchase'
    this.submitButton.attr('disabled', false)
    this.submitButton.text(enableWith)
    this.submitButton.val(enableWith)
  }

  processCreditCard() {
    const $ = window.$
    if (typeof window.Stripe === 'undefined') {
      $('#error_explanation').html('<ul><li>Payment system is still loading. Please try again.</li></ul>').show()
      this.resetButton()
      return
    }

    const cardNumberEl = this.form.querySelector('#card_number_formatted')
    const cardNumber = cardNumberEl?.cleave ? cardNumberEl.cleave.getRawValue() : $('#card_number_formatted').val()?.replace(/\s/g, '') || ''
    const cardCvvEl = this.form.querySelector('#card_cvv_formatted')
    const cardCvv = cardCvvEl?.cleave ? cardCvvEl.cleave.getRawValue() : $('#card_cvv_formatted').val() || ''
    const month = $('#card_month_hidden').val() || $('#card_month').val()
    const year = $('#card_year_hidden').val() || $('#card_year').val()

    window.Stripe.createToken(
      {
        name: $('#card_name').val(),
        number: cardNumber,
        cvc: cardCvv,
        expMonth: month,
        expYear: year
      },
      (status, response) => this.handleStripeResponse(status, response)
    )
  }

  handleStripeResponse(status, response) {
    const $ = window.$
    if (status === 200) {
      $('#stripe_credit_card_token').val(response.id)
      this.form.submit()
    } else {
      if (response.error?.code === 'invalid_number' || response.error?.code === 'incorrect_number') {
        $('#card_number_formatted').parent().addClass('field_with_errors')
        $('#card_number_error').text(response.error.message || 'Invalid card number').show()
      }
      if (response.error?.code === 'invalid_cvc' || response.error?.code === 'incorrect_cvc') {
        $('#card_cvv_formatted').parent().addClass('field_with_errors')
        $('#card_cvv_error').text(response.error.message || 'Invalid CVV').show()
      }
      if (response.error?.code === 'invalid_expiry_month' || response.error?.code === 'invalid_expiry_year') {
        $('#card_expiry_formatted').parent().addClass('field_with_errors')
        $('#card_expiry_error').text(response.error.message || 'Invalid expiry date').show()
      }
      $('#error_explanation').html(`<ul><li>${response.error?.message || 'Card declined'}</li></ul>`).show()
      this.resetButton()
    }
  }
}
